import { Fragment, useContext, useState } from "react";
import ToastContext from "../../context/toastProvider";
import { describePath, formatDiffValue } from "./pricingDiffFormat";
import { computeConfigDiff } from "./usePricingEditor";

const sectionClass = "rounded border border-primary/50 bg-mgs-bg p-4 text-left";

const formatDate = (value) => {
    if (!value) {
        return "-";
    }
    return new Intl.DateTimeFormat("pt-BR").format(new Date(value));
};

const PricingHistory = ({ versions = [], activeVersion, activateVersion }) => {
    const { goodToast, badToast } = useContext(ToastContext);
    const [expandedVersion, setExpandedVersion] = useState(null);

    const activeConfig = versions.find((entry) => entry.version === activeVersion)?.config;

    const handleReactivate = async (version) => {
        if (!window.confirm(`Reativar a versão ${version}? As mudanças listadas passam a valer em até 30s.`)) {
            return;
        }
        const response = await activateVersion(version);
        if (response?.type === "success") {
            goodToast(`Versão ${version} reativada.`);
            setExpandedVersion(null);
        } else {
            badToast(response?.message || "Erro ao reativar versão.");
        }
    };

    return (
        <section className={sectionClass}>
            <h2 className="mb-3 text-xl font-semibold text-[#d59106]">Histórico</h2>
            <table className="w-full divide-y divide-[#d59106]/40 text-left text-sm">
                <thead className="bg-[#111] text-[#d59106]">
                    <tr>
                        <th className="px-3 py-2">Nº</th>
                        <th className="px-3 py-2">Data</th>
                        <th className="px-3 py-2">Autor</th>
                        <th className="px-3 py-2">Nota</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#d59106]/20">
                    {versions.map((entry) => {
                        const isActive = entry.version === activeVersion;
                        const isExpanded = expandedVersion === entry.version;
                        // Diff no sentido do Reativar: da config ativa para a desta versão —
                        // exatamente o que passa a valer se o botão for clicado.
                        const diff = isExpanded && activeConfig && entry.config
                            ? computeConfigDiff(activeConfig, entry.config)
                            : [];
                        return (
                            <Fragment key={entry.version}>
                                <tr>
                                    <td className="px-3 py-2 text-primary">{entry.version}</td>
                                    <td className="px-3 py-2 text-primary">{formatDate(entry.createdAt)}</td>
                                    <td className="px-3 py-2 text-primary">{entry.createdBy || "-"}</td>
                                    <td className="px-3 py-2 text-primary">{entry.note || "-"}</td>
                                    <td className="px-3 py-2">
                                        {isActive ? (
                                            <span className="rounded bg-[#d59106] px-2 py-1 text-xs font-semibold text-black">
                                                ativa
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2">
                                        {!isActive ? (
                                            <div className="flex flex-wrap gap-1">
                                                <button
                                                    type="button"
                                                    className="rounded border border-[#d59106]/60 px-2 py-1 text-xs text-primary hover:bg-[#d59106]/10"
                                                    onClick={() => setExpandedVersion(isExpanded ? null : entry.version)}
                                                >
                                                    {isExpanded ? "ocultar" : "ver mudanças"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="rounded bg-[#d59106] px-2 py-1 text-xs font-semibold text-black hover:bg-[#b47c06]"
                                                    onClick={() => handleReactivate(entry.version)}
                                                >
                                                    Reativar
                                                </button>
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                                {isExpanded ? (
                                    <tr data-testid={`history-diff-${entry.version}`}>
                                        <td colSpan="6" className="bg-black/30 px-3 py-2">
                                            {diff.length === 0 ? (
                                                <p className="text-xs text-primary/70">
                                                    Configuração idêntica à versão ativa — reativar não muda nenhum valor.
                                                </p>
                                            ) : (
                                                <div>
                                                    <p className="mb-1 text-xs font-semibold text-[#d59106]">
                                                        Reativar a versão {entry.version} aplicará estas mudanças:
                                                    </p>
                                                    <ul className="space-y-0.5 text-xs text-primary">
                                                        {diff.map((change) => (
                                                            <li key={change.path}>
                                                                {describePath(change.path)}:{" "}
                                                                <span className="text-primary/60 line-through">{formatDiffValue(change.path, change.before)}</span>
                                                                {" → "}
                                                                <span className="font-semibold">{formatDiffValue(change.path, change.after)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ) : null}
                            </Fragment>
                        );
                    })}
                    {!versions.length ? (
                        <tr>
                            <td colSpan="6" className="px-3 py-6 text-center text-primary/70">
                                Nenhuma versão encontrada.
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
        </section>
    );
};

export default PricingHistory;
